import { type ReactNode, useEffect, useRef } from 'react'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from '@/auth/msal-config'

interface ProtectedRouteProps {
  children: ReactNode
  loadingComponent?: ReactNode
}

/**
 * ProtectedRoute Component - Microsoft Entra ID Authentication
 * 
 * Wraps routes that require authentication.
 * Automatically redirects to Microsoft login if user is not authenticated.
 * 
 * @example
 * ```tsx
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 * ```
 */
export function ProtectedRoute({ 
  children, 
  loadingComponent 
}: ProtectedRouteProps) {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const loginStartedRef = useRef(false)

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return
    }

    if (isAuthenticated || loginStartedRef.current) {
      return
    }

    loginStartedRef.current = true
    sessionStorage.setItem('msal.redirect.path', window.location.pathname)

    void instance.loginRedirect({
      ...loginRequest,
      redirectStartPage: window.location.href
    })
  }, [inProgress, instance, isAuthenticated])

  // Wait for MSAL to finish any in-progress operations
  // This prevents race conditions during authentication flow
  if (inProgress !== InteractionStatus.None) {
    return <>{loadingComponent || <div className="flex items-center justify-center h-screen">Authentifizierung läuft...</div>}</>
  }

  if (!isAuthenticated) {
    return <>{loadingComponent || <div className="flex items-center justify-center h-screen">Weiterleitung zum Login...</div>}</>
  }

  // User is authenticated, render protected content
  return <>{children}</>
}
